import { useState, useEffect } from 'react';

const MyBarChart = ({ 
  width=200,
  height=200,
  strokeWidth=0.5, 
  color="#cccccc", 
  graphOpacity=0.8,
  barOpacity=0.8,
  barColor="#ff4444",
  className="", 
  data=[{x: 0, y: 3}, {x: 1, y: 7}, {x: 2, y: 2}, {x: 3, y: 9}, {x: 4, y: 5}],
  labelPrefix="",
  labelSuffix="",
  barSpacing=0.8 // Space between bars as a fraction of bar width
}) => {
  const [animatedData, setAnimatedData] = useState(data.map(d => ({ ...d, y: 0 })));
  const [largestX, setLargestX] = useState(Math.max(...data.map(d => d.x)));
  const [smallestX, setSmallestX] = useState(Math.min(...data.map(d => d.x)));
  const [largestY, setLargestY] = useState(Math.max(...data.map(d => d.y)));
  const [smallestY, setSmallestY] = useState(Math.min(...data.map(d => d.y)));

  // Generate axis steps from data
  const xAxisSteps = [...new Set(data.map(d => d.x))].sort((a, b) => a - b);
  const yAxisSteps = (() => {
    const minY = Math.min(0, Math.min(...data.map(d => d.y)));
    const maxY = Math.max(...data.map(d => d.y));
    const range = maxY - minY;
    const stepSize = range / 10;
    console.log("Y Axis Steps:", Array.from({ length: 11 }, (_, i) => minY + i * stepSize));
    return Array.from({ length: 11 }, (_, i) => minY + i * stepSize);
  })();

  useEffect(() => {
    const startData = animatedData;
    const endData = data;
    const duration = 500; // 500ms animation for bars
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out for bars growing up)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const interpolatedData = startData.map((startPoint, index) => {
        const endPoint = endData[index] || startPoint;
        return {
          x: startPoint.x + (endPoint.x - startPoint.x) * easeOut,
          y: startPoint.y + (endPoint.y - startPoint.y) * easeOut
        };
      });

      // Handle case where new data array is longer
      if (endData.length > startData.length) {
        for (let i = startData.length; i < endData.length; i++) {
          interpolatedData.push({
            x: endData[i].x,
            y: endData[i].y * easeOut
          });
        }
      } else if (endData.length < startData.length) {
        interpolatedData.splice(endData.length);
      }

      setAnimatedData(interpolatedData);
      setLargestX(Math.max(...endData.map(d => d.x)));
      setSmallestX(Math.min(...endData.map(d => d.x)));
      setLargestY(Math.max(...endData.map(d => d.y)));
      // For bar charts, ensure the baseline starts at 0 or the minimum value, whichever is larger
      setSmallestY(Math.min(0, Math.min(...endData.map(d => d.y))));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [data]);

  // Calculate bar width based on available space and number of bars
  const plotWidth = 220;
  const plotHeight = 100;
  const numBars = animatedData.length;
  const availableWidth = plotWidth / (numBars > 0 ? numBars : 1);
  const barWidth = availableWidth * barSpacing;

  return (
    <div style={{ width: width, height: height }}>
      <svg 
        width="100%"
        height="100%"
        viewBox="0 0 240 120" 
        fill="none"
        className={className}
      >
        {/* X-axis lines (vertical grid) */}
        <g>
          {xAxisSteps.map((step, index) => {
            const x = 10 + (index / Math.max(xAxisSteps.length - 1, 1)) * 220;
            return (
              <line
                key={`x-axis-${index}-${step}`}
                x1={x}
                y1={10}
                x2={x}
                y2={110}
                stroke={color}
                strokeWidth={strokeWidth}
                opacity={index === 0 || index === xAxisSteps.length - 1 ? graphOpacity : graphOpacity*0.3}
              />
            );
          })}
        </g>
        
        {/* Y-axis lines (horizontal grid) */}
        <g>
          {yAxisSteps.map((step, index) => {
            const y = 10 + (index / (yAxisSteps.length - 1)) * 100;
            return (
              <line
                key={`y-axis-${index}-${step}`}
                x1={10}
                y1={y}
                x2={230}
                y2={y}
                stroke={color}
                strokeWidth={strokeWidth}
                opacity={index === 0 || index === yAxisSteps.length - 1 ? graphOpacity : graphOpacity*0.3}
              />
            );
          })}
        </g>
        
        {/* Bars */}
        <g>
          {animatedData.map((point, index) => {
            // Calculate bar position to distribute evenly across the plot width
            const barSpaceWidth = plotWidth / numBars;
            const barX = 10 + (index * barSpaceWidth) + (barSpaceWidth - barWidth) / 2;
            
            // Normalize bar height properly - bars should start from baseline (0 or minimum value)
            const baselineY = Math.max(0, smallestY);
            const normalizedHeight = Math.max(0, point.y - baselineY);
            const barHeight = (normalizedHeight / (largestY - baselineY)) * plotHeight;
            const barY = 110 - barHeight;
            
            return (
              <rect
                key={`bar-${index}`}
                x={barX}
                y={barY}
                width={barWidth}
                height={barHeight}
                fill={barColor}
                opacity={barOpacity}
                stroke={barColor}
                strokeWidth={strokeWidth * 0.5}
              />
            );
          })}
        </g>
        
        {/* X-axis labels (bottom) */}
        <g>
          {xAxisSteps.map((step, index) => {
            // Use the same positioning logic as bars for consistency
            const barSpaceWidth = plotWidth / numBars;
            const x = 10 + (index * barSpaceWidth) + barSpaceWidth / 2;
            const labelValue = step;
            return (
              <text
                key={`x-label-${index}`}
                x={x}
                y={118}
                textAnchor="middle"
                fontSize='6'
                fill={color}
                opacity={graphOpacity*0.8}
              >
                {labelValue}
              </text>
            );
          })}
        </g>
        
        {/* Y-axis labels (left) */}
        <g>
          {yAxisSteps.map((step, index) => {
            const y = 110 - (index / (yAxisSteps.length - 1)) * 100;
            const labelValue = step.toFixed(0);
            const fontSize = Math.max(2, Math.min(5, 12 / (labelValue.length + 1)));
            return (
              <text
                key={`y-label-${index}-${step}`}
                x={4}
                y={y + 1}
                textAnchor="middle"
                fontSize={fontSize}
                fill={color}
                opacity={graphOpacity*0.8}
              >
                {labelPrefix}{labelValue}{labelSuffix}
              </text>
            );
          })}
        </g>

        {/* Value labels on top of bars */}
        <g>
          {animatedData.map((point, index) => {
            // Use the same positioning logic as bars
            const barSpaceWidth = plotWidth / numBars;
            const x = 10 + (index * barSpaceWidth) + barSpaceWidth / 2;
            
            // Use the same normalization for label positioning
            const baselineY = Math.max(0, smallestY);
            const normalizedHeight = Math.max(0, point.y - baselineY);
            const barHeight = (normalizedHeight / (largestY - baselineY)) * plotHeight;
            const labelY = 110 - barHeight - 3;
            const labelValue = point.y.toFixed();
            const fontSize = Math.max(2, Math.min(4, 10 / labelValue.length));
            
            return (
              <text
                key={`value-label-${index}`}
                x={x}
                y={labelY}
                textAnchor="middle"
                fontSize={fontSize}
                fill={barColor}
                opacity={barOpacity}
                fontWeight="bold"
              >
                {labelPrefix}{labelValue}{labelSuffix}
              </text>
            );
          })}
        </g>

        {/* Main axes (emphasized) */}
        <line
          x1={10}
          y1={10}
          x2={10}
          y2={110}
          stroke={color}
          strokeWidth={strokeWidth * 1.5}
          opacity={graphOpacity}
        />
        <line
          x1={10}
          y1={110}
          x2={230}
          y2={110}
          stroke={color}
          strokeWidth={strokeWidth * 1.5}
          opacity={graphOpacity}
        />
      </svg>
    </div>
  );
};

export default MyBarChart;
